import { useAtomValue } from "jotai";
import { nanoid } from "nanoid";
import { ElementRule } from "shared/datamodel/schemas/element-data-layer";
import { undoRedoStackAtom } from "state-atoms";
import { useEffect, useMemo } from "react";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useDynamicElementActions } from "./use-dynamic-element-actions";
import { CanvasElement } from "shared/datamodel/schemas";
import tracking from "frontend/tracking";

export const useDynamicRules = (
  elementId: string,
  element: CanvasElement,
  shouldInitializeDataLayer: boolean = false
) => {
  const { initializeDataLayer } = useDynamicElementActions();
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const data = element?.dataLayer;

  // Since we support rules for non-data-layer elements using elementText,
  // we need to initialize the data layer if it doesn't exist.
  useEffect(() => {
    if (!!element && !data && shouldInitializeDataLayer) {
      initializeDataLayer(elementId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!element, shouldInitializeDataLayer]);

  const memoizedRules = useDeepEqualMemo(data?.rules);

  const activeRules = useMemo(() => {
    return memoizedRules?.filter((rule) => rule.active) ?? [];
  }, [memoizedRules]);

  function changeRule(rule: ElementRule) {
    tracking.trackAnalyticsEvent("data-layer-rule-changed", {
      elementId,
      ruleId: rule.id,
    });

    undoRedoStack?.patchElement(elementId, (element) => {
      if (!element.dataLayer) return;
      const index = element.dataLayer.rules.findIndex((r) => r.id === rule.id);
      if (index !== undefined && index !== -1) {
        element.dataLayer.rules[index] = rule;
      } else {
        element.dataLayer.rules.push(rule);
      }
    });
  }

  function deleteRule(rule: ElementRule) {
    tracking.trackAnalyticsEvent("data-layer-rule-deleted", {
      elementId,
      ruleId: rule.id,
    });

    undoRedoStack?.patchElement(elementId, (element) => {
      if (!element.dataLayer) return;
      element.dataLayer.rules = element.dataLayer.rules.filter((r) => r.id !== rule.id);
    });
  }

  function duplicateRule(rule: ElementRule, newId?: string) {
    tracking.trackAnalyticsEvent("data-layer-rule-duplicated", {
      elementId,
      ruleId: rule.id,
    });

    undoRedoStack?.patchElement(elementId, (element) => {
      if (!element.dataLayer) return;
      const newRule = { ...rule, id: newId ?? nanoid(10) };
      element.dataLayer.rules.push(newRule);
    });
  }

  return {
    rules: memoizedRules ?? [],
    rulesLoaded: !!memoizedRules,
    activeRules,
    changeRule,
    deleteRule,
    duplicateRule,
  };
};
